import React from 'react';
import { Helmet } from 'react-helmet-async';
import PageLayout from '@/components/PageLayout';

const CookiePolicy: React.FC = () => {
  return (
    <PageLayout showFooter={false}>
      <Helmet>
        <title>Cookie Policy - CIPHER Security</title>
        <meta name="description" content="Learn about how CIPHER Security uses cookies and similar technologies on our website." />
      </Helmet>
      
      <div className="container mx-auto px-4 py-32">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold text-white mb-8">Cookie Policy</h1>
          <div className="text-gray-300 space-y-6 leading-relaxed">
            <p className="text-sm text-gray-400">
              <strong>Last Updated:</strong> December 30, 2024
            </p>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">What Are Cookies?</h2>
              <p>
                Cookies are small text files that are placed on your computer or mobile device when you visit our website. 
                They are widely used to make websites work more efficiently and to provide information to website owners.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">How We Use Cookies</h2>
              <p>
                CIPHER Security uses cookies and similar technologies to enhance your browsing experience, analyze website traffic, 
                and provide personalized content. We respect your privacy and provide you with control over your cookie preferences.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Types of Cookies We Use</h2>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold text-white mb-3">1. Necessary Cookies</h3>
                  <p className="mb-3">
                    These cookies are essential for the website to function properly. They cannot be disabled.
                  </p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li><strong>Session Cookies:</strong> Enable basic website functionality and secure contact form submissions</li>
                    <li><strong>Security Cookies:</strong> Protect against cross-site request forgery and other security threats</li>
                    <li><strong>Cookie Consent Preferences:</strong> Remember your cookie preferences and choices</li>
                    <li><strong>Development Access:</strong> Control access to development environments (temporary)</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-white mb-3">2. Analytics Cookies (Optional)</h3>
                  <p className="mb-3">
                    These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously.
                  </p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li><strong>Google Analytics:</strong> Tracks website usage, page views, user behavior, and traffic sources</li>
                    <li><strong>Google Tag Manager:</strong> Manages tracking codes and marketing tags</li>
                    <li><strong>Performance Metrics:</strong> Measures website performance and loading times</li>
                  </ul>
                  <p className="text-sm text-gray-400 mt-2">
                    <em>These cookies can be disabled through your browser settings.</em>
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-white mb-3">3. Marketing/Advertising Cookies (Optional)</h3>
                  <p className="mb-3">
                    These cookies are used to deliver relevant advertisements and marketing content.
                  </p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li><strong>Ad Personalization:</strong> Customize advertisements based on your interests</li>
                    <li><strong>Social Media Integration:</strong> Enable sharing on social media platforms</li>
                    <li><strong>Remarketing:</strong> Show relevant ads when you visit other websites</li>
                  </ul>
                  <p className="text-sm text-gray-400 mt-2">
                    <em>These cookies can be disabled through your browser settings.</em>
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Third-Party Services</h2>
              <p className="mb-4">
                We use the following third-party services that may set cookies:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Google Analytics:</strong> Website analytics and user behavior tracking</li>
                <li><strong>Google Tag Manager:</strong> Tag and tracking code management</li>
                <li><strong>Silktide Consent Manager:</strong> Cookie consent management and GDPR compliance</li>
                <li><strong>Microsoft Teams:</strong> Contact form notifications (server-side only)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Data Collection and Storage</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">Contact Form Data</h3>
                  <p>
                    When you submit our contact form, we collect and store:
                  </p>
                  <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
                    <li>Name and email address</li>
                    <li>Country and message content</li>
                    <li>IP address and timestamp</li>
                    <li>Privacy policy agreement status</li>
                  </ul>
                  <p className="text-sm text-gray-400 mt-2">
                    This data is stored securely and used only for responding to your inquiries.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">Rate Limiting</h3>
                  <p>
                    We implement rate limiting (3 requests per hour per IP address) to prevent spam and abuse. 
                    This information is stored temporarily and automatically deleted.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Managing Your Cookie Preferences</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">Browser Settings</h3>
                  <p>
                    You can control cookies through your browser's settings preferences. 
                    You can usually find these settings in the "Privacy" or "Security" section of your browser's menu.
                  </p>
                  <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
                    <li>Block all cookies</li>
                    <li>Accept only necessary cookies</li>
                    <li>Clear existing cookies</li>
                    <li>Set exceptions for specific websites</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">Contact Us for Cookie Management</h3>
                  <p>
                    If you need assistance managing your cookie preferences or have questions about our cookie usage, 
                    please contact us and we'll help you configure your preferences.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Cookie Retention</h2>
              <div className="space-y-3">
                <p><strong>Session Cookies:</strong> Deleted when you close your browser</p>
                <p><strong>Persistent Cookies:</strong> Stored for varying periods depending on their purpose</p>
                <p><strong>Analytics Cookies:</strong> Typically retained for 2 years (Google Analytics default)</p>
                <p><strong>Consent Preferences:</strong> Stored until you clear them or change your preferences</p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">International Transfers</h2>
              <p>
                Some of our third-party service providers (such as Google Analytics) may transfer and process 
                your data outside of your country of residence. These transfers are conducted in accordance 
                with applicable data protection laws and regulations.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Updates to This Policy</h2>
              <p>
                We may update this Cookie Policy from time to time to reflect changes in our practices, 
                technology, legal requirements, or other factors. We will notify you of any material 
                changes by posting the updated policy on our website.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Contact Us</h2>
              <p>
                If you have any questions about this Cookie Policy or our use of cookies, 
                please contact us:
              </p>
              <div className="mt-4 p-4 bg-gray-800 rounded-lg">
                <p><strong>CIPHER Security</strong></p>
                <p>Email: <a href="mailto:sales@ciphersecurity.io" className="text-blue-400 hover:text-blue-300">sales@ciphersecurity.io</a></p>
                <p>Website: <a href="https://ciphersecurity.io" className="text-blue-400 hover:text-blue-300">https://ciphersecurity.io</a></p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Consent</h2>
              <p>
                By continuing to use our website after being notified of this Cookie Policy, 
                you consent to our use of cookies as described in this policy, subject to your 
                cookie preferences and settings.
              </p>
            </section>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default CookiePolicy;